// App-level error boundary — keeps one crashing page from blanking the whole app.
import { Component, type ReactNode } from 'react';

interface Props { children: ReactNode }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // eslint-disable-next-line no-console
    console.error('App error boundary caught:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#0F172A] text-[#e2e8f0] p-6">
          <div className="max-w-md w-full text-center">
            <div className="text-[#2dd4bf] text-lg font-semibold mb-2">Something went wrong</div>
            <p className="text-[13px] text-[#94a3b8] mb-4">
              This page hit an unexpected error. The rest of the app is fine — head back to modules or reload.
            </p>
            <pre className="text-left text-[11px] text-[#64748b] bg-[#1e293b] rounded-[8px] p-3 overflow-auto max-h-40 whitespace-pre-wrap">
              {this.state.error.message}
            </pre>
            <button
              onClick={() => { this.setState({ error: null }); window.location.assign('/modules'); }}
              className="mt-4 bg-[#2dd4bf] text-[#0f172a] font-medium rounded-[8px] px-4 py-2 text-[13px]"
            >
              Back to modules
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
